import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  IconButton,
  Button,
  Box,
  Menu,
  FormControlLabel,
  Checkbox,
  TextField,
  Typography,
  Card,
  CardContent,
  Breadcrumbs,
  Stack,
  CircularProgress,
  Alert,
  Fade,
  Paper,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Switch,
  TableSortLabel,
  OutlinedInput,
  Divider,
  Popover,
} from "@mui/material";

import EditIcon from "@mui/icons-material/Edit";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import { Link, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewColumnIcon from "@mui/icons-material/ViewColumn";
import { BUTTON_STYLES } from "../../styles/componentStyles";
import NavBar from "../NavBar";
import api from "../../axiosInstance";
import { useTheme } from "@emotion/react";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteConfirmationDialog from "../DeleteConfirmationDialog";
import { toast, ToastContainer } from "react-toastify";
import CloseIcon from "@mui/icons-material/Close";
import NoteAddIcon from "@mui/icons-material/NoteAdd";
import BankDetailsModal from "./BankDetailsModal";
import AssuredWorkloadIcon from "@mui/icons-material/AssuredWorkload";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import PersonRemoveIcon from "@mui/icons-material/PersonRemove";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import BarChartIcon from "@mui/icons-material/BarChart";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventNoteIcon from "@mui/icons-material/EventNote";
import TuneIcon from "@mui/icons-material/Tune";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import MentorAnalytics from "./MentorAnalytics";
import MentorAvailabilityDialog from "./MentorAvailabilityDialog";
import MentorAvailabilityListDialog from "./MentorAvailabilityListDialog";
import MentorSessionLimitDialog from "./MentorSessionLimitDialog";
import MentorPackageDiscountDialog from "./MentorPackageDiscountDialog";
import MentorSessionPriceDialog from "./MentorSessionPriceDialog";
// Column config and visibility state
const allColumns = [
  {
    key: "mentorId",
    label: "ID",
    cellCss: { align: "center", minWidth: "60px" },
    getValue: (r) => r.mentorId,
  },
  {
    key: "mentorName",
    label: "Full Name",
    cellCss: { align: "center", minWidth: "180px" },
    getValue: (r) => r.mentorName,
  },
  {
    key: "scheduledSessions",
    label: "Scheduled Sessions",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.scheduledSessions,
  },
  {
    key: "completedSessions",
    label: "Completed Sessions",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.completedSessions,
  },
  {
    key: "rescheduledSessions",
    label: "Rescheduled Sessions",
    cellCss: { align: "center", minWidth: "170px" },
    getValue: (r) => r.rescheduledSessions,
  },
  {
    key: "cancelledSessions",
    label: "Cancelled Sessions",
    cellCss: { align: "center", minWidth: "170px" },
    getValue: (r) => r.cancelledSessions,
  },
  {
    key: "upcomingSessions",
    label: "Upcoming Sessions",
    cellCss: { align: "center", minWidth: "170px" },
    getValue: (r) => r.upcomingSessions,
  },
  {
    key: "occupancyPercentage",
    label: "Occupancy %",
    cellCss: { align: "center", minWidth: "140px" },
    getValue: (r) => `${r.occupancyPercentage}%`,
  },
  {
    key: "availabilityHours",
    label: "Availability (Hrs)",
    cellCss: { align: "center", minWidth: "150px" },
    getValue: (r) => r.availabilityHours,
  },
  {
    key: "sessionTypes",
    label: "Session Types",
    cellCss: { align: "center", minWidth: "220px" },
    getValue: (r) => r.sessionTypes || [],
  },
  {
    key: "packages",
    label: "Packages",
    cellCss: { align: "center", minWidth: "220px" },
    getValue: (r) => r.packages || [],
  },
  {
    key: "status",
    label: "Status",
    cellCss: { align: "center", minWidth: "120px" },
    getValue: (r) => r.status,
  },
  {
    key: "universities",
    label: "Universities",
    cellCss: { align: "center", minWidth: "220px" },
    getValue: (r) => r.universities?.map((u) => u.name).join(", ") || "-",
  },
  {
    key: "courses",
    label: "Courses",
    cellCss: { align: "center", minWidth: "220px" },
    getValue: (r) => r.courses?.map((c) => c.courseName).join(", ") || "-",
  },
  {
    key: "coreSubjects",
    label: "Core Subjects",
    cellCss: { align: "center", minWidth: "220px" },
    getValue: (r) =>
      r.coreSubjects?.map((s) => s.subjectName).join(", ") || "-",
  },
  {
    key: "subjectsTaught",
    label: "Subjects Taught",
    cellCss: { align: "center", minWidth: "280px" },
    getValue: (r) =>
      r.subjectsTaught?.map((s) => s.groupSubjectTitle).join(", ") || "-",
  },
];
const defaultVisibleKeys = new Set([
  "mentorId",
  "mentorName",
  "scheduledSessions",
  "completedSessions",
  "rescheduledSessions",
  "cancelledSessions",
  "upcomingSessions",
  "occupancyPercentage",
  "availabilityHours",
  "status",
  "sessionTypes",
  "packages",
  "universities",
  "courses",
  "coreSubjects",
  "subjectsTaught",
]);

const STORAGE_KEY = "MentorsSessionsVisibleColumns";
const FILTER_KEY = "MentorAdminTableFilters";

const loadFilters = () => {
  try { return JSON.parse(localStorage.getItem(FILTER_KEY)) ?? {}; } catch { return {}; }
};

const numColor = {
  scheduledSessions: { color: "#1565c0", bg: "#e3f2fd" },
  completedSessions: { color: "#2e7d32", bg: "#e8f5e9" },
  rescheduledSessions: { color: "#e65100", bg: "#fff3e0" },
  cancelledSessions: { color: "#c62828", bg: "#ffebee" },
  upcomingSessions: { color: "#6a1b9a", bg: "#f3e5f5" },
  occupancyPercentage: { color: "#00695c", bg: "#e0f2f1" },
  availabilityHours: { color: "#4527a0", bg: "#ede7f6" },
};

const sessionTypeCfg = {
  ONE_ON_ONE: { label: "1:1", color: "#0277bd", bg: "#e1f5fe" },
  GROUP: { label: "Group", color: "#558b2f", bg: "#f1f8e9" },
  DOUBT: { label: "Doubt", color: "#f57f17", bg: "#fffde7" },
};
// Preferred order for the known types above; anything else (future session
// types) is appended after, in whatever order the API returns it.
const sessionTypeOrder = ["ONE_ON_ONE", "GROUP", "DOUBT"];
// Fallback palette so new/unknown session types still get a distinct color
// instead of falling back to plain gray.
const sessionTypePalette = [
  { color: "#6a1b9a", bg: "#f3e5f5" },
  { color: "#ad1457", bg: "#fce4ec" },
  { color: "#00695c", bg: "#e0f2f1" },
  { color: "#4527a0", bg: "#ede7f6" },
  { color: "#c62828", bg: "#ffebee" },
];
const formatSessionTypeLabel = (type) =>
  (type || "")
    .toLowerCase()
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
const getSessionTypeCfg = (type, fallbackIndex) =>
  sessionTypeCfg[type] ?? {
    label: formatSessionTypeLabel(type),
    ...sessionTypePalette[fallbackIndex % sessionTypePalette.length],
  };
const orderSessionTypes = (list) => {
  const arr = Array.isArray(list) ? list : [];
  const known = sessionTypeOrder
    .map((t) => arr.find((s) => s.sessionType === t))
    .filter(Boolean);
  const rest = arr.filter((s) => !sessionTypeOrder.includes(s.sessionType));
  return [...known, ...rest];
};

const statusCfg = {
  ACTIVE: { label: "Active", color: "#2e7d32", bg: "#e8f5e9", dot: "#4caf50" },
  INACTIVE: {
    label: "Inactive",
    color: "#c62828",
    bg: "#ffebee",
    dot: "#ef5350",
  },
  PENDING: {
    label: "Pending",
    color: "#e65100",
    bg: "#fff3e0",
    dot: "#ff9800",
  },
};

function NumCell({ value, colKey }) {
  const cfg = numColor[colKey];
  if (!cfg)
    return <Typography sx={{ fontSize: "0.82rem" }}>{value}</Typography>;
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        px: 1.2,
        py: 0.3,
        borderRadius: "20px",
        background: cfg.bg,
        border: `1px solid ${cfg.color}22`,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.8rem",
          fontWeight: 700,
          color: cfg.color,
          lineHeight: 1,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

const SESSION_TYPES_VISIBLE = 2;
const SESSION_TYPES_INLINE_LIMIT = 3;

const SESSION_TYPES_GRID_COLS = "44px 76px 1fr";

function SessionTypeRow({ session, cfg }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: SESSION_TYPES_GRID_COLS,
        alignItems: "center",
        columnGap: 1.2,
        px: 1,
        py: 0.4,
        borderRadius: "8px",
        background: cfg.bg,
        border: `1px solid ${cfg.color}22`,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: cfg.color,
          textAlign: "left",
        }}
      >
        {cfg.label}
      </Typography>
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 600,
          color: cfg.color,
          textAlign: "center",
        }}
      >
        {session.sessionCount ?? 0}/{session.sessionLimit ?? 0}
      </Typography>
      <Typography
        sx={{ fontSize: "0.72rem", color: cfg.color, textAlign: "right" }}
      >
        {session.averagePrice != null
          ? `₹${Number(session.averagePrice).toFixed(2)}`
          : "-"}
      </Typography>
    </Box>
  );
}

function SessionTypesHeader() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: SESSION_TYPES_GRID_COLS,
        alignItems: "center",
        columnGap: 1.2,
        px: 1,
        pb: 0.4,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 700,
          color: "#999",
          textAlign: "left",
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        Type
      </Typography>
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 700,
          color: "#999",
          textAlign: "center",
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        Count/Limit
      </Typography>
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 700,
          color: "#999",
          textAlign: "right",
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        Avg Price
      </Typography>
    </Box>
  );
}

function SessionTypesCell({ types }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const rows = orderSessionTypes(types);

  if (!rows.length)
    return <Typography sx={{ fontSize: "0.82rem" }}>-</Typography>;

  // Few session types: show the full breakdown directly in the table,
  // same as the popover design below. Only collapse to chips + hover
  // popup once it grows past the inline limit.
  if (rows.length <= SESSION_TYPES_INLINE_LIMIT) {
    return (
      <Box sx={{ minWidth: 190 }}>
        <SessionTypesHeader />
        <Stack spacing={0.5} alignItems="stretch">
          {rows.map((s, i) => (
            <SessionTypeRow
              key={s.sessionType}
              session={s}
              cfg={getSessionTypeCfg(s.sessionType, i)}
            />
          ))}
        </Stack>
      </Box>
    );
  }

  const visible = rows.slice(0, SESSION_TYPES_VISIBLE);
  const overflowCount = rows.length - visible.length;
  const open = Boolean(anchorEl);
  const openPopover = (e) => setAnchorEl(e.currentTarget);
  const closePopover = () => setAnchorEl(null);

  return (
    <>
      <Stack
        direction="row"
        spacing={0.6}
        justifyContent="center"
        flexWrap="wrap"
        useFlexGap
        onMouseEnter={openPopover}
        onClick={openPopover}
        sx={{ cursor: "pointer", minWidth: 150 }}
      >
        {visible.map((s, i) => {
          const cfg = getSessionTypeCfg(s.sessionType, i);
          return (
            <Chip
              key={s.sessionType}
              size="small"
              label={`${cfg.label} · ${s.sessionCount ?? 0}/${s.sessionLimit ?? 0}`}
              sx={{
                fontSize: "0.7rem",
                fontWeight: 700,
                color: cfg.color,
                background: cfg.bg,
                border: `1px solid ${cfg.color}22`,
              }}
            />
          );
        })}
        <Chip
          size="small"
          label={`View More +${overflowCount}`}
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: "#1565c0",
            background: "#e3f2fd",
          }}
        />
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        disableRestoreFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            borderRadius: 2,
            boxShadow: "0 12px 40px rgba(0,0,0,0.13)",
            minWidth: 220,
          },
        }}
      >
        <SessionTypesHeader />
        <Stack spacing={0.5} alignItems="stretch">
          {rows.map((s, i) => (
            <SessionTypeRow
              key={s.sessionType}
              session={s}
              cfg={getSessionTypeCfg(s.sessionType, i)}
            />
          ))}
        </Stack>
      </Popover>
    </>
  );
}

const PACKAGES_VISIBLE = 2;
const PACKAGES_INLINE_LIMIT = 3;
const PACKAGES_GRID_COLS = "1fr 76px";
const PACKAGE_COLOR = "#6a1b9a";
const PACKAGE_BG = "#f3e5f5";

function PackageRow({ pkg }) {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: PACKAGES_GRID_COLS,
        alignItems: "center",
        columnGap: 1.2,
        px: 1,
        py: 0.4,
        borderRadius: "8px",
        background: PACKAGE_BG,
        border: `1px solid ${PACKAGE_COLOR}22`,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.72rem",
          fontWeight: 700,
          color: PACKAGE_COLOR,
          textAlign: "left",
        }}
      >
        {pkg.packageName}
      </Typography>
      <Typography
        sx={{ fontSize: "0.72rem", fontWeight: 600, color: PACKAGE_COLOR, textAlign: "right" }}
      >
        {pkg.packageDiscount > 0 ? `${pkg.packageDiscount}% off` : "-"}
      </Typography>
    </Box>
  );
}

function PackagesHeader() {
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: PACKAGES_GRID_COLS,
        alignItems: "center",
        columnGap: 1.2,
        px: 1,
        pb: 0.4,
      }}
    >
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 700,
          color: "#999",
          textAlign: "left",
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        Package
      </Typography>
      <Typography
        sx={{
          fontSize: "0.62rem",
          fontWeight: 700,
          color: "#999",
          textAlign: "right",
          textTransform: "uppercase",
          letterSpacing: 0.3,
        }}
      >
        Discount
      </Typography>
    </Box>
  );
}

function PackagesCell({ packages }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const rows = Array.isArray(packages) ? packages : [];

  if (!rows.length)
    return <Typography sx={{ fontSize: "0.82rem" }}>-</Typography>;

  if (rows.length <= PACKAGES_INLINE_LIMIT) {
    return (
      <Box sx={{ minWidth: 190 }}>
        <PackagesHeader />
        <Stack spacing={0.5} alignItems="stretch">
          {rows.map((p) => (
            <PackageRow key={p.packageId} pkg={p} />
          ))}
        </Stack>
      </Box>
    );
  }

  const visible = rows.slice(0, PACKAGES_VISIBLE);
  const overflowCount = rows.length - visible.length;
  const open = Boolean(anchorEl);
  const openPopover = (e) => setAnchorEl(e.currentTarget);
  const closePopover = () => setAnchorEl(null);

  return (
    <>
      <Stack
        direction="row"
        spacing={0.6}
        justifyContent="center"
        flexWrap="wrap"
        useFlexGap
        onMouseEnter={openPopover}
        onClick={openPopover}
        sx={{ cursor: "pointer", minWidth: 150 }}
      >
        {visible.map((p) => (
          <Chip
            key={p.packageId}
            size="small"
            label={
              p.packageDiscount > 0
                ? `${p.packageName} · ${p.packageDiscount}%`
                : p.packageName
            }
            sx={{
              fontSize: "0.7rem",
              fontWeight: 700,
              color: PACKAGE_COLOR,
              background: PACKAGE_BG,
              border: `1px solid ${PACKAGE_COLOR}22`,
            }}
          />
        ))}
        <Chip
          size="small"
          label={`View More +${overflowCount}`}
          sx={{
            fontSize: "0.7rem",
            fontWeight: 700,
            color: "#1565c0",
            background: "#e3f2fd",
          }}
        />
      </Stack>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={closePopover}
        disableRestoreFocus
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        PaperProps={{
          sx: {
            mt: 1,
            p: 1,
            borderRadius: 2,
            boxShadow: "0 12px 40px rgba(0,0,0,0.13)",
            minWidth: 220,
          },
        }}
      >
        <PackagesHeader />
        <Stack spacing={0.5} alignItems="stretch">
          {rows.map((p) => (
            <PackageRow key={p.packageId} pkg={p} />
          ))}
        </Stack>
      </Popover>
    </>
  );
}

function StatusCell({ value }) {
  const v = (value ?? "").toUpperCase();
  const cfg = statusCfg[v] ?? {
    label: value ?? "—",
    color: "#555",
    bg: "#f5f5f5",
    dot: "#9e9e9e",
  };
  return (
    <Box
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.6,
        px: 1.2,
        py: 0.4,
        borderRadius: "20px",
        background: cfg.bg,
      }}
    >
      <Box
        sx={{
          width: 7,
          height: 7,
          borderRadius: "50%",
          background: cfg.dot,
          flexShrink: 0,
        }}
      />
      <Typography
        sx={{ fontSize: "0.78rem", fontWeight: 700, color: cfg.color }}
      >
        {cfg.label}
      </Typography>
    </Box>
  );
}

function MentorAdminTable({ startDate, endDate }) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [colleges, setColleges] = useState([]);
  const [subjectCategories, setSubjectCategories] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const handleNewClose = () => setOpenNew(false);
  const _f = loadFilters();
  const [filterSubjectCategory, setFilterSubjectCategory] = useState(_f.filterSubjectCategory ?? "");
  const [filterSubject, setFilterSubject] = useState(_f.filterSubject ?? "");
  const [mentorName, setMentorName] = useState(_f.mentorName ?? "");
  const [page, setPage] = useState(_f.page ?? 0);
  const [rowsPerPage, setRowsPerPage] = useState(_f.rowsPerPage ?? 30);
  const [dense, setDense] = useState(_f.dense ?? true);
  const [coreSubjectList, setCoreSubjectList] = useState([]);
  const [groupedSubjectList, setGroupedSubjectList] = useState([]);
  const [subjectCategoryList, setSubjectCategoryList] = useState([]);
  const effectiveStart = startDate ?? "";
  const effectiveEnd = endDate ?? "";

  // Persist filters to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(FILTER_KEY, JSON.stringify({ mentorName, filterSubjectCategory, filterSubject, page, rowsPerPage, dense }));
    } catch {}
  }, [mentorName, filterSubjectCategory, filterSubject, page, rowsPerPage, dense]);

  const isFirstMount = React.useRef(true);

  // Reset page when date props change (skip on first mount)
  useEffect(() => {
    if (isFirstMount.current) { isFirstMount.current = false; return; }
    setPage(0);
  }, [effectiveStart, effectiveEnd]);

  const [searchQuery, setSearchQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [openImage, setOpenImage] = useState(null);
  const [subjectCategoriesEdit, setSubjectCategoriesEdit] = useState(null);
  const [orderBy, setOrderBy] = useState("category");
  const [order, setOrder] = useState("asc");
  const [bankViewRow, setBankViewRow] = useState(null);
  const [openBankView, setOpenBankView] = useState(false);
  const [openBankEdit, setOpenBankEdit] = useState(false);
  const [bankEditData, setBankEditData] = useState(null);
  const [openAnalytics, setOpenAnalytics] = useState(false);
  const [analyticsRow, setAnalyticsRow] = useState(null);
  const [availabilityRow, setAvailabilityRow] = useState(null);
  const [viewAvailabilityRow, setViewAvailabilityRow] = useState(null);
  const [sessionLimitRow, setSessionLimitRow] = useState(null);
  const [packageDiscountRow, setPackageDiscountRow] = useState(null);
  const [sessionPriceRow, setSessionPriceRow] = useState(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [MentorDetails, setMentorDetails] = useState([]);
  const [totalMentors, setTotalMentors] = useState(0);

  console.log(MentorDetails);

  // Inside sorted Data your component
  const sortedData = useMemo(() => {
    if (!MentorDetails) return [];

    return [...MentorDetails].sort((a, b) => {
      if (!orderBy) return 0;

      const aValue = a[orderBy];
      const bValue = b[orderBy];

      if (aValue == null) return 1;
      if (bValue == null) return -1;

      if (typeof aValue === "string") {
        return order === "asc"
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }

      if (typeof aValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });
  }, [MentorDetails, orderBy, order]);

  // Sorting logic
  const handleSort = (columnKey) => {
    const isAsc = orderBy === columnKey && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(columnKey);
  };

  // padding Dense
  const handleChangeDense = (event) => {
    setDense(event.target.checked);
  };

  // Fetch core subjects for Subject Category dropdown
  useEffect(() => {
    api
      .get("/mentor-catalog/coreSubjects")
      .then((res) => setCoreSubjectList(res.data.data ?? []))
      .catch((err) => console.error("Core subjects fetch error:", err));
  }, []);

  // Fetch grouped subjects when coreSubjectId changes
  useEffect(() => {
    if (!filterSubjectCategory) {
      setGroupedSubjectList([]);
      setFilterSubject("");
      return;
    }
    api
      .get("/mentor-catalog/groupedSubjects", {
        params: { core_subject_id: filterSubjectCategory },
      })
      .then((res) => setGroupedSubjectList(res.data.data ?? []))
      .catch((err) => console.error("Grouped subjects fetch error:", err));
  }, [filterSubjectCategory]);

  // Fetch mentor details report
  useEffect(() => {
    setIsLoading(true);
    const params = { page, size: rowsPerPage };
    if (effectiveStart) params.startDate = effectiveStart;
    if (effectiveEnd) params.endDate = effectiveEnd;
    if (mentorName) params.mentorName = mentorName;
    if (filterSubjectCategory) params.coreSubjectId = filterSubjectCategory;
    if (filterSubject) params.subject = filterSubject;
    api
      .get(`/admin/mentors/details-report`, { params })
      .then((res) => {
        setMentorDetails(res.data.data.content);
        setTotalMentors(res.data.data.totalElements);
      })
      .catch((error) => {
        console.error("Mentor details fetch error:", error);
        setError("Failed to load mentors. Please try again.");
      })
      .finally(() => setIsLoading(false));
  }, [
    effectiveStart,
    effectiveEnd,
    page,
    rowsPerPage,
    mentorName,
    filterSubjectCategory,
    filterSubject,
    refreshToken,
  ]);

  // Initialize from localStorage synchronously to avoid a race with the persister effect
  const [visibleColumns, setVisibleColumns] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const arr = JSON.parse(raw);
        if (Array.isArray(arr)) {
          const validKeys = new Set(allColumns.map((c) => c.key));
          const filtered = arr.filter((k) => validKeys.has(k));
          if (filtered.length > 0) {
            return new Set(filtered);
          }
        }
      }
    } catch {}
    return new Set(Array.from(defaultVisibleKeys));
  });

  const [columnsMenuAnchor, setColumnsMenuAnchor] = useState(null);
  const openColumnsMenu = Boolean(columnsMenuAnchor);

  // Persist column config to localStorage on change
  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(Array.from(visibleColumns)),
      );
    } catch {}
  }, [visibleColumns]);

  const handleOpenColumnsMenu = (e) => setColumnsMenuAnchor(e.currentTarget);
  const handleCloseColumnsMenu = () => setColumnsMenuAnchor(null);
  const toggleColumn = (key) => {
    setVisibleColumns((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Helper function to get boolean chip styling
  const getBooleanChip = (value) => {
    const normalized = value === true || value === "true" || value === "Yes";
    return (
      <Chip
        label={normalized ? "Yes" : "No"}
        color={normalized ? "success" : "error"}
        size="small"
        variant="outlined"
      />
    );
  };

  // Helper function to get content type chip styling
  const getContentTypeChip = (type) => {
    const typeConfig = {
      text: { color: "primary", label: "Text" },
      image: { color: "secondary", label: "Image" },
      video: { color: "success", label: "Video" },
      link: { color: "info", label: "Link" },
    };

    const config = typeConfig[type?.toLowerCase()] || {
      color: "default",
      label: type || "Unknown",
    };
    return <Chip label={config.label} color={config.color} size="small" />;
  };
  //   Page nation handel changes
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value));
    setPage(0);
  };

  // pagenation code
  // const sortedData = sortedData;

  // Columns Field showing obeject
  const visibleColumnDefs = allColumns.filter((c) => visibleColumns.has(c.key));
  // Deletion confirm dialog box open and close.
  const handleClickOpen = (id) => {
    setSelectedId(id);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setSelectedId(null);
  };
  // Delete The banner record api fetch data
  const handleDelete = async () => {
    try {
      await api.delete(`/api/v1/subjectCategories/${selectedId}`);

      // If deletion is successful, update local state update
      setSubjectCategories((prev) =>
        prev.filter((row) => row.id !== selectedId),
      );
      toast.success("Record deleted successfully.!", {
        position: "top-right",
        autoClose: 3000,
      });
      setOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      toast.error("Failed to delete the record. Please try again.");
    }
  };
  const handleToggleActive = async (row) => {
    try {
      const updated = { ...row, active: !row.active };
      await api.put(`/api/v1/mentors/${row.id}`, updated);
      setSubjectCategories((prev) =>
        prev.map((item) =>
          item.id === row.id ? { ...item, active: !item.active } : item,
        ),
      );
      toast.success(
        `Mentor ${!row.active ? "activated" : "deactivated"} successfully!`,
      );
    } catch (error) {
      toast.error("Failed to update status. Please try again.");
    }
  };
  // opening Adding page data in onclick
  const handleNewOpen = () => {
    setOpenNew(true);
    setSubjectCategoriesEdit([]);
  };
  // Editing page data in onclick
  const handleEditClick = (rowData) => {
    handleNewOpen();
    setSubjectCategoriesEdit(rowData);
  };
  // Success show th response values
  const handleFormSuccess = (newItem) => {
    if (!newItem || !newItem.id) {
      console.warn("Invalid item received from form", newItem);
      return;
    }
    setSubjectCategories((prev) => {
      const exists = prev.some((item) => item.id === newItem.id);
      console.log(exists);
      if (exists) {
        return prev.map((item) => (item.id === newItem.id ? newItem : item));
      } else {
        return [newItem, ...prev];
      }
    });

    setSubjectCategoriesEdit(null);
  };
  return (
    <>
      {/* <NavBar /> */}
      <ToastContainer />
      {/* Search Section */}
      <Card sx={{ margin: "0 15px", boxShadow: 1 }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: 2,
              flexWrap: "wrap",
            }}
          >
            <Box
              sx={{
                display: "flex",
                gap: 1,
                flex: 1,
                flexWrap: "wrap",
                alignItems: "center",
              }}
            >
              <TextField
                size="small"
                label="Mentor Name"
                placeholder="Search by mentor name..."
                value={mentorName}
                onChange={(e) => {
                  setMentorName(e.target.value);
                  setPage(0);
                }}
                sx={{ minWidth: 220 }}
              />
              <FormControl
                size="small"
                sx={{ minWidth: 220, textAlign: "left" }}
              >
                <InputLabel>Core Subjects</InputLabel>
                <Select
                  label="Core Subjects"
                  value={filterSubjectCategory}
                  onChange={(e) => {
                    setFilterSubjectCategory(e.target.value);
                    setFilterSubject("");
                    setPage(0);
                  }}
                  sx={{ "& .MuiSelect-select": { textAlign: "left" } }}
                >
                  <MenuItem value="">All</MenuItem>
                  {coreSubjectList.map((cat) => (
                    <MenuItem key={cat.id} value={cat.id}>
                      {cat.subjectName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl
                size="small"
                sx={{ minWidth: 220, textAlign: "left" }}
              >
                <InputLabel>Grouped Subjects</InputLabel>
                <Select
                  label="Grouped Subjects"
                  value={filterSubject}
                  onChange={(e) => {
                    setFilterSubject(e.target.value);
                    setPage(0);
                  }}
                  disabled={!filterSubjectCategory}
                  sx={{ "& .MuiSelect-select": { textAlign: "left" } }}
                >
                  <MenuItem value="">All</MenuItem>
                  {groupedSubjectList.map((s) => (
                    <MenuItem key={s.id} value={s.id}>
                      {s.groupSubjectTitle}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography
                sx={{ display: { xs: "none", md: "block" }, textAlign: "left" }}
                variant="body2"
                color="text.secondary"
              >
                {sortedData.length} of {totalMentors} Mentors
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  startIcon={<ViewColumnIcon />}
                  onClick={handleOpenColumnsMenu}
                  sx={{ fontSize: { xs: "10px", md: "12px" } }}
                >
                  Columns
                </Button>
                <Menu
                  anchorEl={columnsMenuAnchor}
                  open={openColumnsMenu}
                  onClose={handleCloseColumnsMenu}
                  PaperProps={{
                    sx: {
                      width: 200,
                      maxHeight: 500,
                      overflowY: "auto",
                      borderRadius: 2,
                    },
                  }}
                >
                  {allColumns.map((col) => (
                    <FormControlLabel
                      key={col.key}
                      control={
                        <Checkbox
                          checked={visibleColumns.has(col.key)}
                          onChange={() => toggleColumn(col.key)}
                        />
                      }
                      label={col.label}
                      sx={{ pl: 1, pr: 2 }}
                    />
                  ))}
                </Menu>
                {/* <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleNewOpen}
                  {...BUTTON_STYLES.primary}
                  sx={{ fontSize: { xs: "10px", md: "12px" } }}
                >
                  Add Mentors
                </Button> */}
              </Stack>
              <Tooltip title="Dense padding" arrow>
                <FormControlLabel
                  control={
                    <Switch
                      checked={dense}
                      onChange={handleChangeDense}
                      color="primary"
                    />
                  }
                />
              </Tooltip>
            </Box>
          </Box>
          <Typography
            sx={{ display: { xs: "block", md: "none" }, textAlign: "left" }}
            variant="body2"
            color="text.secondary"
          >
            {sortedData.length} of {totalMentors} Mentors
          </Typography>
        </CardContent>
      </Card>
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      )}
      {!isLoading && (
        <Fade in={!isLoading}>
          <Paper
            sx={{
              width: "98%",
              mx: "auto",
              overflow: "hidden",
              boxShadow: 2,
              marginBottom: "20px",
            }}
          >
            <TableContainer sx={{ maxHeight: "calc(100vh - 320px)", overflowX: "auto" }}>
              <Table
                stickyHeader
                aria-label="news table"
                size={dense ? "small" : "medium"}
              >
                <TableHead>
                  <TableRow>
                    {visibleColumnDefs.map((c) => (
                      <TableCell
                        key={c.key}
                        sx={{
                          padding: "10px 5px",
                          backgroundColor: "#17479e",
                          color: "white",
                          fontWeight: 600,
                          position: "sticky",
                          top: 0,
                          zIndex: 3,
                          ...c.cellCss,
                        }}
                        sortDirection={orderBy === c.key ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === c.key}
                          direction={orderBy === c.key ? order : "asc"}
                          onClick={() => handleSort(c.key)}
                          sx={{
                            color: "#ffffff !important",
                            "&.Mui-active": {
                              color: "#ffffff !important",
                            },
                            "& .MuiTableSortLabel-icon": {
                              color: "#ffffff !important",
                            },
                          }}
                        >
                          {c.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}
                    <TableCell
                      sx={{
                        backgroundColor: "#17479e",
                        color: "white",
                        fontWeight: 600,
                        minWidth: 120,
                        textAlign: "center",
                        position: "sticky",
                        top: 0,
                        right: 0,
                        zIndex: 4,
                      }}
                    >
                      Actions
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {console.log(sortedData)}
                  {Array.isArray(sortedData) && sortedData.length > 0 ? (
                    sortedData.map((row) => (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row.id}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: "rgba(0, 0, 0, 0.02)",
                          },
                          "&:hover": {
                            backgroundColor: "rgba(0, 0, 0, 0.04)",
                          },
                        }}
                      >
                        {visibleColumnDefs.map((c) => (
                          <TableCell
                            key={c.key}
                            sx={{ padding: "10px 10px", ...c.cellCss }}
                          >
                            {(() => {
                              const value = c.getValue(row);
                              const key = c.key;

                              // Optional debug

                              if (key === "viewable" || key === "isNational") {
                                return getBooleanChip(value);
                              }

                              if (key === "contentType") {
                                return getContentTypeChip(value);
                              }

                              if (key === "image") {
                                return key === "image" ? (
                                  <img
                                    src={`https://siaedutech.com/public/${value}`}
                                    alt="preview"
                                    style={{
                                      width: "60px",
                                      height: "auto",
                                      borderRadius: "4px",
                                      cursor: "pointer",
                                    }}
                                    onClick={() =>
                                      setOpenImage(
                                        `https://siaedutech.com/public/${value}`,
                                      )
                                    }
                                  />
                                ) : (
                                  value // show fallback text if not valid image
                                );
                              }

                              if (key === "status")
                                return <StatusCell value={value} />;
                              if (key === "sessionTypes")
                                return <SessionTypesCell types={value} />;
                              if (key === "packages")
                                return <PackagesCell packages={value} />;
                              if (numColor[key] !== undefined)
                                return <NumCell value={value} colKey={key} />;
                              return value;
                            })()}
                          </TableCell>
                        ))}
                        <TableCell
                          sx={{
                            align: "center",
                            padding: "10px 10px",
                            minWidth: "120px",
                            position: "sticky",
                            backgroundColor: "#ffffff",
                            right: 0,
                            zIndex: 1,
                          }}
                        >
                          <Stack
                            direction="row"
                            spacing={0.5}
                            justifyContent="center"
                          >
                            <Tooltip
                              title="Mentor Analytics"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="primary"
                                onClick={() =>
                                  navigate("/mentorship/analytics", {
                                    state: {
                                      mentorData: row,
                                      startDate,
                                      endDate,
                                    },
                                  })
                                }
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <BarChartIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Set Availability"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="success"
                                onClick={() => setAvailabilityRow(row)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "success.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <EventAvailableIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="View Availabilities"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="primary"
                                onClick={() => setViewAvailabilityRow(row)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <EventNoteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Change Session Limit"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="warning"
                                onClick={() => setSessionLimitRow(row)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "warning.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <TuneIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Update Package Discount"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="secondary"
                                onClick={() => setPackageDiscountRow(row)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "secondary.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <LocalOfferIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip
                              title="Manage Session Prices"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="info"
                                onClick={() => setSessionPriceRow(row)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "info.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <CurrencyRupeeIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            {/* <Tooltip
                              title="Edit Subject Category"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="primary"
                                onClick={() => handleEditClick(row)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "primary.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip> */}

                            {/* <Tooltip
                              title="Delete Subject Category"
                              arrow
                              placement="top"
                            >
                              <IconButton
                                color="error"
                                onClick={() => handleClickOpen(row.id)}
                                size="small"
                                sx={{
                                  "&:hover": {
                                    backgroundColor: "error.light",
                                    color: "white",
                                  },
                                }}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip> */}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow>
                      <TableCell
                        colSpan={visibleColumnDefs.length + 1}
                        sx={{ textAlign: "center", py: 4 }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                          }}
                        >
                          <Typography variant="h6" color="text.secondary">
                            {mentorName
                              ? "No mentors found matching your search"
                              : "No mentors available"}
                          </Typography>
                          {mentorName && (
                            <Button
                              variant="outlined"
                              onClick={() => setMentorName("")}
                              startIcon={<SearchIcon />}
                            >
                              Clear Search
                            </Button>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[10, 30, 50, 100]}
              component="div"
              count={totalMentors}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{
                borderTop: "1px solid",
                borderColor: "divider",
                "& .MuiTablePagination-toolbar": {
                  paddingLeft: 2,
                  paddingRight: 2,
                },
              }}
            />
          </Paper>
        </Fade>
      )}

      {/* Bank Details View Dialog */}
      <Dialog
        open={openBankView}
        onClose={() => setOpenBankView(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontWeight: 600,
          }}
        >
          Bank Details
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title="Edit Bank Details" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  setOpenBankView(false);
                  setBankEditData(bankViewRow);
                  setOpenBankEdit(true);
                }}
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton size="small" onClick={() => setOpenBankView(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent>
          {[
            {
              label: "Account Holder Name",
              value: bankViewRow?.accountHolderName,
            },
            { label: "Bank Name", value: bankViewRow?.bankName },
            {
              label: "Account Number",
              value: bankViewRow?.accountNumber,
            },
            {
              label: "IFSC Code",
              value: bankViewRow?.ifscCode,
            },
            {
              label: "Branch Name",
              value: bankViewRow?.branchName,
            },
            {
              label: "Account Type",
              value: bankViewRow?.accountType,
            },
          ].map(({ label, value }) => (
            <Box
              key={label}
              sx={{ display: "flex", justifyContent: "space-between", py: 0.8 }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontWeight: 500 }}
              >
                {label}
              </Typography>
              <Typography variant="body2">{value || "—"}</Typography>
            </Box>
          ))}
        </DialogContent>
      </Dialog>

      {/* Bank Details Edit Modal */}
      <BankDetailsModal
        open={openBankEdit}
        onClose={() => setOpenBankEdit(false)}
        subjectCategoriesEdit={bankEditData}
        onSuccess={(updated) => {
          setBankViewRow(updated);
          setOpenBankEdit(false);
        }}
      />

      {/* Banner modal */}
      <BankDetailsModal
        open={openNew}
        onClose={handleNewClose}
        subjectCategoriesEdit={subjectCategoriesEdit}
        onSuccess={handleFormSuccess}
      />
      {/* Confirm Delete Dialog */}
      <DeleteConfirmationDialog
        open={open}
        onClose={handleClose}
        onConfirm={handleDelete}
        title="Confirm Delete"
        message="Are you sure you want to delete this news item? This action cannot be undone."
        confirmText="DELETE"
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
      <Dialog
        open={!!openImage}
        onClose={() => setOpenImage(null)}
        maxWidth="md"
      >
        <DialogContent>
          <Box position="relative">
            <IconButton
              aria-label="close"
              onClick={() => setOpenImage(null)}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
            <img
              src={openImage}
              alt="Preview"
              style={{ width: "100%", height: "auto", borderRadius: 4 }}
            />
          </Box>
        </DialogContent>
      </Dialog>

      <MentorAvailabilityDialog
        open={Boolean(availabilityRow)}
        mentorRow={availabilityRow}
        onClose={() => setAvailabilityRow(null)}
        onSuccess={() => setRefreshToken((t) => t + 1)}
      />
      <MentorAvailabilityListDialog
        open={Boolean(viewAvailabilityRow)}
        mentorRow={viewAvailabilityRow}
        onClose={() => setViewAvailabilityRow(null)}
      />
      <MentorSessionLimitDialog
        open={Boolean(sessionLimitRow)}
        mentorRow={sessionLimitRow}
        onClose={() => setSessionLimitRow(null)}
        onSuccess={() => setRefreshToken((t) => t + 1)}
      />
      <MentorPackageDiscountDialog
        open={Boolean(packageDiscountRow)}
        mentorRow={packageDiscountRow}
        onClose={() => setPackageDiscountRow(null)}
        onSuccess={() => setRefreshToken((t) => t + 1)}
      />
      <MentorSessionPriceDialog
        open={Boolean(sessionPriceRow)}
        mentorRow={sessionPriceRow}
        onClose={() => setSessionPriceRow(null)}
      />
    </>
  );
}

export default MentorAdminTable;
